'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ElementType } from 'react';
import { Link } from '@skin-seoul/design-system';
import {
  formatCurrency,
  type Currency,
  type CartItemResponse,
  CLASSNAME,
  productSizeAndPackTextConverter,
} from '@skin-seoul/service';
import { debounce, isNil, isNotNil } from 'es-toolkit';
import { useDidUpdate, Condition, extractPathname } from '@skin-seoul/utils';
import CartNumberInput from './CartNumberInput';
import CartProductCardSkeleton from './CartProductCardSkeleton';

const CART_ITEM_CARD_TEST_ID = 'cart-item-card';
const REMOVE_ITEM_FROM_CART_TEST_ID = 'remove-item-from-cart';
const CART_ITEM_NUMBER_INPUT_TEST_ID = 'cart-item-number-input';

interface CartProductCardProps {
  item: CartItemResponse;
  onQuantityChange: (quantity: number) => void;
  onRemoveItem: () => void;
  onImageClick?: (productLink: string) => void;
  currency: Currency;
  ImageComponent?: React.ComponentType<{ src: string; alt: string; width: number; height: number }>;
  linkComponent?: ElementType;
}

const CartProductCard = ({
  item,
  currency,
  onRemoveItem,
  onQuantityChange,
  ImageComponent = ({ src, alt, width, height }) => <img src={src} alt={alt} width={width} height={height} />,
  linkComponent,
}: CartProductCardProps) => {
  const LinkComponent = linkComponent || Link;
  const normalizedProductLink = extractPathname(item.productLink);
  const productSizeAndPackText = productSizeAndPackTextConverter({
    size: item.itemData?.size,
    pack: item.itemData?.pack,
  });

  const [itemCounter, setItemCounter] = React.useState<number | undefined>(item.quantity);
  const [value, setValue] = useState<number>(item.quantity);
  const prevValue = useRef<number>(item.quantity);

  const itemMax = 9999;
  const itemMin = item.quantityLimit?.min ?? 1;

  const debouncedQuantityUpdate = useMemo(
    () =>
      debounce(
        (quantity: number) => {
          if (quantity === item.quantity) {
            return;
          }
          if (isNil(quantity) || isNaN(quantity)) {
            return;
          }
          if (quantity < itemMin) {
            return onQuantityChange(itemMin);
          }
          if (quantity > itemMax) {
            return onQuantityChange(itemMax);
          }
          onQuantityChange(quantity);
        },
        1400,
        {
          edges: ['trailing'],
        },
      ),
    [item.quantity, itemMin, itemMax, onQuantityChange],
  );

  const handleQuantityChange = (valueAsNumber: number) => {
    debouncedQuantityUpdate(valueAsNumber);
  };

  const handleRemoveItem = () => {
    onRemoveItem();
  };

  useDidUpdate(() => {
    setItemCounter(item.quantity);

    if (prevValue.current !== item.quantity) {
      setValue(item.quantity);
      prevValue.current = item.quantity;
    }
  }, [item.quantity]);

  useDidUpdate(() => {
    if (isNotNil(itemCounter)) {
      handleQuantityChange(itemCounter);
    }
  }, [itemCounter]);

  useEffect(() => {
    return () => {
      debouncedQuantityUpdate.cancel();
    };
  }, [debouncedQuantityUpdate]);

  return (
    <div className="flex w-full gap-3 relative pt-4 px-0" data-testid={CART_ITEM_CARD_TEST_ID}>
      <LinkComponent
        href={normalizedProductLink}
        className="flex items-center justify-center min-w-[112px] w-[112px] h-[112px] border border-neutral-100 rounded-[8px] p-1 cursor-pointer no-underline hover:no-underline"
      >
        <div className="flex items-center justify-center w-full h-full overflow-hidden">
          <ImageComponent src={item.image[0].thumbnail} alt={item.name} width={100} height={100} />
        </div>
      </LinkComponent>

      <div className="flex flex-col w-full justify-between">
        <div className="flex flex-col justify-between w-full mb-3">
          <div className="flex flex-row gap-1 items-center justify-between">
            <p className={`text-paragraph-medium line-clamp-1 ${CLASSNAME.NOT_TRANSLATE}`}>{item.name}</p>
            <button
              onClick={handleRemoveItem}
              aria-label="Remove item"
              data-testid={REMOVE_ITEM_FROM_CART_TEST_ID}
              className="inline-flex items-center justify-center rounded-md h-6 w-6 hover:bg-neutral-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="45" fill="white" stroke="black" strokeWidth="4" />
                <path d="M35 35 L65 65 M65 35 L35 65" stroke="black" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <Condition
            expression={Boolean(productSizeAndPackText)}
            then={
              <span className={`text-label-small text-neutral-600 ${CLASSNAME.NOT_TRANSLATE}`}>
                {productSizeAndPackText}
              </span>
            }
          />
        </div>

        <div className="flex justify-between items-center">
          <CartNumberInput
            data-testid={CART_ITEM_NUMBER_INPUT_TEST_ID}
            min={1}
            value={isNaN(value) ? '' : String(value)}
            defaultValue={String(item.quantity)}
            max={itemMax}
            onValueChange={({ valueAsNumber }) => {
              setValue(valueAsNumber);
              setItemCounter(valueAsNumber);
            }}
          />
          <p className={`text-paragraph-medium ${CLASSNAME.NOT_TRANSLATE}`}>
            {formatCurrency(currency, item.price.salePrice)}
          </p>
        </div>
      </div>
    </div>
  );
};

CartProductCard.Skeleton = CartProductCardSkeleton;

export default CartProductCard;
